import React from "react";

const BATCH_INTERVAL_MS = 5000;
const BATCH_MAX_SIZE = 50;
const sessionId = crypto.randomUUID();

type TrackEventCreate = {
  event_type: "Click";
  event_action: string;
  session_id: string;
  timestamp: string;
  url: string;
};

let eventQueue: TrackEventCreate[] = [];
let trackingEnabled = false;

function takeQueuedEvents() {
  const events = eventQueue;
  eventQueue = [];
  return events;
}

function flushEvents() {
  if (eventQueue.length === 0) return;

  fetch("/api/activity/batch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ events: takeQueuedEvents() }),
    keepalive: true,
  }).catch(() => undefined);
}

export function trackEvent(eventAction: string) {
  if (!trackingEnabled) return;

  eventQueue.push({
    event_type: "Click",
    event_action: eventAction,
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    url: window.location.pathname,
  });

  if (eventQueue.length >= BATCH_MAX_SIZE) flushEvents();
}

function startTracking() {
  trackingEnabled = true;

  function trackClick(event: MouseEvent) {
    if (!(event.target instanceof Element)) return;

    const trackedElement = event.target.closest("[data-track]") as HTMLElement | null;
    const action = trackedElement?.dataset.track;
    if (!trackedElement || !action) return;

    const contextElement = trackedElement.closest("[data-track-context]") as HTMLElement | null;
    const context = contextElement?.dataset.trackContext;
    if (!context) {
      if (import.meta.env.DEV) console.warn("Tracked element is missing data-track-context", trackedElement);
      return;
    }

    trackEvent(`${context}.${action}`);
  }

  function flushHiddenEvents() {
    if (document.visibilityState !== "hidden" || eventQueue.length === 0) return;

    const payload = JSON.stringify({ events: takeQueuedEvents() });
    navigator.sendBeacon("/api/activity/batch", new Blob([payload], { type: "application/json" }));
  }

  const interval = window.setInterval(flushEvents, BATCH_INTERVAL_MS);
  document.addEventListener("click", trackClick, { capture: true });
  document.addEventListener("visibilitychange", flushHiddenEvents);

  return () => {
    trackingEnabled = false;
    window.clearInterval(interval);
    document.removeEventListener("click", trackClick, { capture: true });
    document.removeEventListener("visibilitychange", flushHiddenEvents);
    flushEvents();
  };
}

export function useTrackEvents(enabled: boolean) {
  React.useEffect(() => {
    if (!enabled) return;

    return startTracking();
  }, [enabled]);
}
